import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Timer as TimerIcon,
  QuestionMark as QuestionIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Send as SubmitIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as ExitFullscreenIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showError, showSuccess, showInfo } from '../../utils/sweetAlertConfig';

const DigitalAssessment = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exitWarningOpen, setExitWarningOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [warningCount, setWarningCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  
  // Refs
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const exitAttemptRef = useRef(0);

  // Load assessment data
  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  // Timer effect
  useEffect(() => {
    if (hasStarted && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [hasStarted, timeRemaining]);

  // Auto-save effect
  useEffect(() => {
    if (hasStarted && Object.keys(answers).length > 0) {
      setAutoSaveStatus('saving');
      
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
      }
      
      autoSaveRef.current = setTimeout(() => {
        saveProgress();
      }, 2000); // Auto-save every 2 seconds after changes
    }
  }, [answers, hasStarted]);

  // Fullscreen event listeners
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      setIsFullscreen(isCurrentlyFullscreen);
      
      // If user exits fullscreen during exam, show warning
      if (!isCurrentlyFullscreen && hasStarted && timeRemaining > 0) {
        handleExitAttempt();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [hasStarted, timeRemaining]);

  // Prevent page refresh/close during exam
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasStarted && timeRemaining > 0) {
        e.preventDefault();
        e.returnValue = 'Your assessment is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasStarted, timeRemaining]);

  const loadAssessment = async () => {
    try {
      const response = await api.get(`/candidate/assessment/${assessmentId}`);
      const data = response.data;
      
      setAssessment(data.assessment);
      setQuestions(data.questions || []);
      setTimeRemaining(data.assessment.duration_minutes * 60);
      
      // Load saved progress if exists
      if (data.savedAnswers) {
        setAnswers(data.savedAnswers);
      }
      
    } catch (error) {
      console.error('Error loading assessment:', error);
      showError('Error', 'Failed to load assessment. Please try again.');
      navigate('/candidate/applications');
    } finally {
      setIsLoading(false);
    }
  };

  const enterFullscreen = async () => {
    try {
      const element = document.documentElement;
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  const handleStartAssessment = async () => {
    await enterFullscreen();
    setHasStarted(true);
    
    // Record start time
    try {
      await api.post(`/candidate/assessment/${assessmentId}/start`);
    } catch (error) {
      console.error('Error recording start time:', error);
    }
  };

  const handleExitAttempt = () => {
    exitAttemptRef.current += 1;
    setWarningCount(exitAttemptRef.current);
    
    if (exitAttemptRef.current >= 3) {
      showError(
        'Assessment Terminated',
        'You have exceeded the maximum number of exit attempts. Your assessment will be submitted automatically.',
        () => handleForceSubmit()
      );
    } else {
      setExitWarningOpen(true);
    }
  };

  const handleWarningClose = (returnToFullscreen = false) => {
    setExitWarningOpen(false);
    if (returnToFullscreen) {
      enterFullscreen();
    }
  };

  const saveProgress = async () => {
    try {
      await api.post(`/candidate/assessment/${assessmentId}/save`, {
        answers: answers,
        currentQuestion: currentQuestionIndex,
        timeRemaining: timeRemaining
      });
      setAutoSaveStatus('saved');
    } catch (error) {
      console.error('Error saving progress:', error);
      setAutoSaveStatus('error');
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleTimeUp = () => {
    showInfo('Time Up!', 'The assessment time has ended. Your answers will be submitted automatically.');
    handleForceSubmit();
  };

  const handleForceSubmit = async () => {
    await submitAssessment(true);
  };

  const handleSubmit = () => {
    setSubmitDialogOpen(true);
  };

  const submitAssessment = async (forced = false) => {
    try {
      setIsLoading(true);
      
      await api.post(`/candidate/assessment/${assessmentId}/submit`, {
        answers: answers,
        forced: forced,
        exitAttempts: exitAttemptRef.current
      });

      await exitFullscreen();
      
      // Clear any saved data
      localStorage.removeItem(`assessment_${assessmentId}_answers`);
      localStorage.removeItem(`assessment_${assessmentId}_time`);
      localStorage.removeItem(`assessment_${assessmentId}_started`);
      
      showSuccess(
        'شكراً لك! 🎉',
        'تم تسليم الامتحان بنجاح. شكراً لمشاركتك معنا.',
        () => {
          // Close the window/tab
          setTimeout(() => {
            window.close();
            // If window.close() doesn't work (some browsers block it), redirect to a thank you page
            window.location.href = 'about:blank';
          }, 1000);
        }
      );
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      showError('Error', 'Failed to submit assessment. Please try again.');
    } finally {
      setIsLoading(false);
      setSubmitDialogOpen(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    const totalTime = assessment?.duration_minutes * 60 || 1;
    const timePercentage = (timeRemaining / totalTime) * 100;
    
    if (timePercentage > 50) return 'success';
    if (timePercentage > 20) return 'warning';
    return 'error';
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(answers).length;

  if (isLoading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Typography variant="h6">Loading assessment...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (!hasStarted) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box 
          sx={{ 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3
          }}
        >
          <Card sx={{ maxWidth: 800, width: '100%', boxShadow: 12, borderRadius: 4 }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <QuestionIcon sx={{ fontSize: 120, color: 'primary.main', mb: 3 }} />
              
              <Typography variant="h3" gutterBottom sx={{ 
                fontWeight: 700, 
                color: '#29204C',
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2
              }}>
                {assessment?.title}
              </Typography>
              
              <Typography variant="h6" color="text.secondary" sx={{ 
                mb: 4, 
                fontSize: '1.3rem',
                lineHeight: 1.6
              }}>
                You are about to start a digital assessment. Please read the instructions carefully.
              </Typography>

              <Grid container spacing={3} sx={{ mb: 5 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 4, 
                    bgcolor: 'rgba(0, 0, 0, 0.3)', 
                    borderRadius: 3,
                    boxShadow: 4,
                    textAlign: 'center',
                    border: '2px solid rgba(255, 255, 255, 0.5)'
                  }}>
                    <TimerIcon sx={{ fontSize: 60, mb: 2, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      fontSize: '1.4rem', 
                      mb: 1,
                      color: '#ffffff',
                      fontWeight: 700
                    }}>
                      Duration
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: '#ffffff'
                    }}>
                      {assessment?.duration_minutes} minutes
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ 
                    p: 4, 
                    bgcolor: 'rgba(0, 0, 0, 0.3)', 
                    borderRadius: 3,
                    boxShadow: 4,
                    textAlign: 'center',
                    border: '2px solid rgba(255, 255, 255, 0.5)'
                  }}>
                    <QuestionIcon sx={{ fontSize: 60, mb: 2, color: '#ffffff' }} />
                    <Typography variant="h6" sx={{ 
                      fontSize: '1.4rem', 
                      mb: 1,
                      color: '#ffffff',
                      fontWeight: 700
                    }}>
                      Questions
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: '#ffffff'
                    }}>
                      {questions.length} questions
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 5, 
                  textAlign: 'left',
                  borderRadius: 3,
                  '& .MuiAlert-message': {
                    fontSize: '1.1rem'
                  }
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ 
                  fontWeight: 700,
                  fontSize: '1.3rem',
                  mb: 2
                }}>
                  Important Instructions:
                </Typography>
                <Box component="ul" sx={{ 
                  margin: 0, 
                  paddingLeft: 3,
                  '& li': {
                    fontSize: '1.1rem',
                    mb: 1.5,
                    lineHeight: 1.6
                  }
                }}>
                  <li>The assessment will automatically enter <strong>fullscreen mode</strong></li>
                  <li>Do <strong>NOT</strong> exit fullscreen or switch tabs during the assessment</li>
                  <li>Your progress will be <strong>auto-saved</strong> every few seconds</li>
                  <li>You have <strong>{assessment?.number_of_trials || 1} attempt(s)</strong> for this assessment</li>
                  <li>Exiting fullscreen <strong>3 times</strong> will automatically submit your assessment</li>
                </Box>
              </Alert>

              <Button
                variant="contained"
                size="large"
                onClick={handleStartAssessment}
                startIcon={<FullscreenIcon sx={{ fontSize: 30 }} />}
                sx={{ 
                  px: 6,
                  py: 2.5,
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  background: 'linear-gradient(45deg, #29204C, #4a3f7a)',
                  boxShadow: 6,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1a1530, #29204C)',
                    transform: 'scale(1.05)',
                    boxShadow: 8
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Assessment
              </Button>
            </CardContent>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: '#29204C',
        position: 'relative'
      }}>
        {/* Header with timer and progress */}
        <Paper 
          elevation={4}
          sx={{ 
            p: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            borderRadius: 0,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
          }}
        >
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <TimerIcon 
                  color={getTimeColor()} 
                  sx={{ fontSize: 40 }}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Time Remaining
                  </Typography>
                  <Typography 
                    variant="h4" 
                    color={getTimeColor() === 'error' ? 'error' : 'text.primary'}
                    sx={{ 
                      fontWeight: 700,
                      fontFamily: 'monospace'
                    }}
                  >
                    {formatTime(timeRemaining)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h6" color="text.primary" gutterBottom sx={{ 
                  fontWeight: 600,
                  fontSize: '1.2rem'
                }}>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6
                    }
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ 
                  mt: 1,
                  fontSize: '1rem'
                }}>
                  {Math.round(progress)}% Complete
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
                <Chip 
                  icon={autoSaveStatus === 'saved' ? <CheckIcon /> : <SaveIcon />}
                  label={autoSaveStatus === 'saved' ? 'Saved' : autoSaveStatus === 'saving' ? 'Saving...' : 'Error'}
                  color={autoSaveStatus === 'saved' ? 'success' : autoSaveStatus === 'saving' ? 'warning' : 'error'}
                  sx={{ 
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                />
                
                {warningCount > 0 && (
                  <Chip 
                    icon={<WarningIcon />}
                    label={`${warningCount}/3`}
                    color="error"
                    sx={{ 
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}
                  />
                )}
                
                <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                  <IconButton 
                    onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                    color="primary"
                    sx={{ 
                      bgcolor: 'rgba(41, 32, 76, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(41, 32, 76, 0.2)'
                      }
                    }}
                  >
                    {isFullscreen ? <ExitFullscreenIcon sx={{ fontSize: 28 }} /> : <FullscreenIcon sx={{ fontSize: 28 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Question content */}
        <Box sx={{ p: 4 }}>
          {currentQuestion && (
            <Card sx={{ 
              maxWidth: 900, 
              mx: 'auto', 
              boxShadow: 6,
              borderRadius: 4,
              border: '1px solid rgba(41, 32, 76, 0.1)'
            }}>
              <CardContent sx={{ p: 6 }}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" gutterBottom sx={{ 
                    fontWeight: 700, 
                    mb: 3,
                    color: '#29204C',
                    lineHeight: 1.4,
                    fontSize: { xs: '1.5rem', md: '2rem' }
                  }}>
                    {currentQuestion.question_text}
                  </Typography>
                  
                  <Chip 
                    label={`${currentQuestion.marks} Marks`}
                    color="primary"
                    size="medium"
                    sx={{ 
                      fontSize: '1rem',
                      fontWeight: 600,
                      px: 2
                    }}
                  />
                </Box>

                {/* Multiple Choice */}
                {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={answers[currentQuestion.question_id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.question_id, e.target.value)}
                      sx={{ gap: 2 }}
                    >
                      {currentQuestion.options.map((option, index) => (
                        <Paper
                          key={index}
                          elevation={1}
                          sx={{
                            border: answers[currentQuestion.question_id] === option.option_text 
                              ? '2px solid #29204C' 
                              : '2px solid transparent',
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': { 
                              boxShadow: 3,
                              bgcolor: 'rgba(41, 32, 76, 0.05)'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={option.option_text}
                            control={<Radio sx={{ 
                              ml: 2,
                              color: '#29204C',
                              '&.Mui-checked': {
                                color: '#68B977'
                              }
                            }} />}
                            label={
                              <Typography sx={{ 
                                fontSize: '1.2rem',
                                fontWeight: 500,
                                py: 2,
                                pr: 3
                              }}>
                                {option.option_text}
                              </Typography>
                            }
                            sx={{ 
                              m: 0,
                              width: '100%',
                              alignItems: 'flex-start'
                            }}
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}

                {/* True/False */}
                {currentQuestion.question_type === 'true_false' && (
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={answers[currentQuestion.question_id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.question_id, e.target.value)}
                      sx={{ gap: 2 }}
                    >
                      {['true', 'false'].map((value) => (
                        <Paper
                          key={value}
                          elevation={answers[currentQuestion.question_id] === value ? 4 : 1}
                          sx={{
                            border: answers[currentQuestion.question_id] === value 
                              ? '3px solid #68B977' 
                              : '2px solid rgba(41, 32, 76, 0.2)',
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            bgcolor: answers[currentQuestion.question_id] === value 
                              ? 'rgba(104, 185, 119, 0.1)' 
                              : '#ffffff',
                            '&:hover': { 
                              boxShadow: 4,
                              bgcolor: answers[currentQuestion.question_id] === value 
                                ? 'rgba(104, 185, 119, 0.15)' 
                                : 'rgba(41, 32, 76, 0.05)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={value}
                            control={<Radio sx={{ 
                              ml: 2,
                              color: '#29204C',
                              '&.Mui-checked': {
                                color: '#68B977'
                              }
                            }} />}
                            label={
                              <Typography sx={{ 
                                fontSize: '1.2rem',
                                fontWeight: 500,
                                py: 2,
                                pr: 3,
                                textTransform: 'capitalize'
                              }}>
                                {value}
                              </Typography>
                            }
                            sx={{ 
                              m: 0,
                              width: '100%',
                              alignItems: 'center'
                            }}
                          />
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}

                {/* Short Answer */}
                {(currentQuestion.question_type === 'short_answer' || currentQuestion.question_type === 'essay') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={currentQuestion.question_type === 'essay' ? 8 : 4}
                    value={answers[currentQuestion.question_id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.question_id, e.target.value)}
                    placeholder="Type your answer here..."
                    variant="outlined"
                    sx={{ 
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                        lineHeight: 1.6,
                        '& fieldset': {
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: '#29204C'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#29204C'
                        }
                      }
                    }}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Button
              variant="outlined"
              startIcon={<PrevIcon sx={{ fontSize: 24, color: '#ffffff' }} />}
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderWidth: 2,
                borderRadius: 3,
                borderColor: '#ffffff',
                color: '#ffffff',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#68B977',
                  color: '#68B977',
                  transform: 'scale(1.02)',
                  '& .MuiSvgIcon-root': {
                    color: '#68B977'
                  }
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Previous
            </Button>

            {currentQuestionIndex < questions.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<NextIcon sx={{ fontSize: 24, color: '#ffffff' }} />}
                onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  boxShadow: 4,
                  bgcolor: '#68B977',
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: '#5a9e67',
                    boxShadow: 6,
                    transform: 'scale(1.02)'
                  }
                }}
              >
                Next Question
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<SubmitIcon sx={{ fontSize: 24, color: '#ffffff' }} />}
                onClick={handleSubmit}
                sx={{ 
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  boxShadow: 4,
                  bgcolor: '#68B977',
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: '#5a9e67',
                    boxShadow: 6,
                    transform: 'scale(1.02)'
                  }
                }}
              >
                Submit Assessment
              </Button>
            )}
          </Box>

          
        </Box>

        {/* Exit Warning Dialog */}
        <Dialog 
          open={exitWarningOpen} 
          onClose={() => {}} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4, p: 2 }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'error.main'
          }}>
            <WarningIcon sx={{ fontSize: 40 }} color="error" />
            Assessment Security Warning
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ 
              fontSize: '1.2rem',
              fontWeight: 600,
              mb: 2
            }}>
              You have exited fullscreen mode during the assessment. This action has been recorded.
            </Typography>
            <Alert severity="error" sx={{ 
              mt: 3,
              borderRadius: 3,
              '& .MuiAlert-message': {
                fontSize: '1.1rem'
              }
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                <strong>Warning {warningCount}/3:</strong> If you exit fullscreen 3 times, 
                your assessment will be automatically submitted with current answers.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button 
              onClick={() => handleWarningClose(false)} 
              variant="outlined"
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  color: '#b71c1c',
                  bgcolor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              Submit Now
            </Button>
            <Button 
              onClick={() => handleWarningClose(true)} 
              variant="contained"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: 4,
                bgcolor: '#29204C',
                color: '#ffffff',
                '&:hover': {
                  bgcolor: '#1e1638',
                  boxShadow: 6
                }
              }}
            >
              Return to Assessment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Submit Confirmation Dialog */}
        <Dialog 
          open={submitDialogOpen} 
          onClose={() => setSubmitDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4, p: 2 }
          }}
        >
          <DialogTitle sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'success.main',
            textAlign: 'center'
          }}>
            Submit Assessment
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Typography variant="h6" gutterBottom sx={{
              fontSize: '1.2rem',
              fontWeight: 600,
              mb: 2,
              textAlign: 'center'
            }}>
              Are you sure you want to submit your assessment?
            </Typography>
            
            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 3, 
              borderRadius: 3, 
              mt: 3,
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                Assessment Summary
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                You have answered <strong>{answeredCount}</strong> out of <strong>{questions.length}</strong> questions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ 
                mt: 2,
                fontSize: '1rem'
              }}>
                ⚠️ Once submitted, you cannot make any changes to your answers.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
            <Button 
              onClick={() => setSubmitDialogOpen(false)}
              variant="outlined"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: '#666666',
                color: '#666666',
                '&:hover': {
                  borderColor: '#333333',
                  color: '#333333',
                  bgcolor: 'rgba(102, 102, 102, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => submitAssessment(false)} 
              variant="contained"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: 4,
                bgcolor: '#68B977',
                color: '#ffffff',
                '&:hover': {
                  bgcolor: '#5a9e67',
                  boxShadow: 6
                }
              }}
            >
              ✅ Submit Assessment
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default DigitalAssessment;